import { initializeApp, getApps } from "firebase/app";
import { getAnalytics, logEvent, setUserId, type Analytics } from "firebase/analytics";

import {
  firebaseAPIKey,
  firebaseAuthDomain,
  firebaseMeasurementId,
  firebaseAppId,
  firebaseProjectId,
  firebaseStorageBucket,
  firebaseMessagingSenderId,
  firebaseKey,
  isProd,
} from "@/client/constants/firebase";

let analytics: Analytics | null = null;

function getFirebaseAnalytics(): Analytics | null {
  if (typeof window !== "undefined") {
    if (analytics) {
      return analytics;
    }
    if (firebaseAPIKey && firebaseMeasurementId) {
      try {
        const app = getApps().length
          ? getApps()[0]
          : initializeApp({
              apiKey: firebaseAPIKey,
              authDomain: firebaseAuthDomain,
              projectId: firebaseProjectId,
              appId: firebaseAppId,
              measurementId: firebaseMeasurementId,
              storageBucket: firebaseStorageBucket,
              messagingSenderId: firebaseMessagingSenderId,
            });
        analytics = getAnalytics(app);
      } catch {
        return null;
      }
      return analytics;
    }
  }
  return null;
}

// Set Firebase Analytics user id when known; clear with null on logout.
export function setAnalyticsUserId(userId?: string | number | null) {
  const instance = getFirebaseAnalytics();
  if (instance) {
    if (userId != null) {
      setUserId(instance, String(userId));
      return;
    }
    setUserId(instance, null);
  }
}

// Log Firebase standard page_view for SPA route changes.
export function trackStandardPageView(path: string) {
  const instance = getFirebaseAnalytics();
  if (instance) {
    logEvent(instance, "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      ...(!isProd ? { debug_mode: true } : {}),
    });
  }
}

export function trackPageView({
  userType,
  pageName,
  userId,
  params,
}: {
  userType: string;
  pageName: string;
  userId?: string | number | null;
  params?: Record<string, string | number | null | undefined>;
}) {
  const instance = getFirebaseAnalytics();
  if (instance) {
    if (userId != null) {
      setUserId(instance, String(userId));
    }
    const eventName = `${firebaseKey}_${userType}_${pageName}`.slice(0, 40);
    logEvent(instance, eventName, {
      user_id: userId != null ? String(userId) : "",
      ...(params ?? {}),
      ...(!isProd ? { debug_mode: true } : {}),
    });
  }
}

export function trackEvent({
  userType,
  apiName,
  userId,
  success,
  resourceId,
}: {
  userType: string;
  apiName: string;
  userId?: string | number | null;
  success?: boolean;
  resourceId?: string | number | null;
}) {
  const instance = getFirebaseAnalytics();
  if (instance) {
    if (userId != null) {
      setUserId(instance, String(userId));
    }
    const eventName = `${firebaseKey}_${userType}_${apiName}`.slice(0, 40);
    logEvent(instance, eventName, {
      user_id: userId != null ? String(userId) : "",
      success: success ? 1 : 0,
      ...(resourceId != null ? { resource_id: String(resourceId) } : {}),
      ...(!isProd ? { debug_mode: true } : {}),
    });
  }
}
